import React from 'react';
import { APP_BG, APP_HEX } from '@/constant/theme/colors.constant';

interface Props {
    rows?: number;
    cols: number;
    hasExpand?: boolean;
    /** Show a sort-bar placeholder matching the real sticky sort bar */
    hasSortBar?: boolean;
}

const WIDE_WIDTHS = [72, 68, 42, 78, 36, 55, 48, 40, 60, 35];
const NARROW_WIDTHS = [55, 50, 36, 60, 30, 42, 38, 30, 45, 28];

// Deterministic pseudo-jitter so adjacent bars don't pulse in perfect unison
const jitter = (ri: number, ci: number, base: number) =>
    base + ((ri * 3 + ci * 7) % 5) * 0.011;

// Sort-chip widths: mimic 2-4 column names at realistic pixel widths
const CHIP_WIDTHS = [54, 72, 48, 64];

const SkeletonCards: React.FC<Props> = ({
    rows = 6,
    cols,
    hasExpand = false,
    hasSortBar = false,
}) => {
    const bodyCols = Math.min(Math.max(cols - 1, 0), 7);

    return (
        <>
            <style>{`
                @keyframes skWave {
                    0%   { transform: translateX(-100%); }
                    60%  { transform: translateX(100%); }
                    100% { transform: translateX(100%); }
                }
                .sk-bar {
                    position: relative;
                    overflow: hidden;
                    background-color: ${APP_HEX.skeletonBar};
                    border-radius: 9999px;
                }
                .sk-rect {
                    position: relative;
                    overflow: hidden;
                    background-color: ${APP_HEX.skeletonBar};
                    border-radius: 8px;
                }
                .sk-bar::after,
                .sk-rect::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255,127,48,0.14) 50%,
                        transparent 100%
                    );
                    animation: skWave 1.6s ease-in-out var(--sk-delay, 0s) infinite;
                }
                .dark .sk-bar,
                .dark .sk-rect { background-color: ${APP_HEX.skeletonDarkBar}; }
                .dark .sk-bar::after,
                .dark .sk-rect::after {
                    background: linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255,127,48,0.22) 50%,
                        transparent 100%
                    );
                }
            `}</style>

            {/* ── Sort bar skeleton ──────────────────────────────────────── */}
            {hasSortBar && (
                <div
                    className={[
                        'sticky top-0 z-20 flex items-stretch',
                        'border-b border-gray-100 dark:border-white/[0.06]',
                        APP_BG.elevated,
                    ].join(' ')}
                >
                    {/* "Sort" label column */}
                    <div className="shrink-0 px-3 flex items-center border-r border-gray-100 dark:border-white/[0.06]">
                        <div
                            className="sk-bar h-2"
                            style={{ width: '26px', '--sk-delay': '0s' } as React.CSSProperties}
                        />
                    </div>
                    {/* Chip row */}
                    <div className="flex items-center divide-x divide-gray-100 dark:divide-white/[0.06] overflow-x-hidden">
                        {CHIP_WIDTHS.map((w, i) => (
                            <div key={i} className="flex items-center gap-1.5 px-3 py-2.5">
                                <div
                                    className="sk-bar h-2.5"
                                    style={{ width: `${w}px`, '--sk-delay': `${(i * 0.06).toFixed(3)}s` } as React.CSSProperties}
                                />
                                {/* chevron icon stub */}
                                <div
                                    className="sk-bar h-2.5 w-2.5 opacity-25 shrink-0"
                                    style={{ '--sk-delay': `${(i * 0.06).toFixed(3)}s` } as React.CSSProperties}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Card list ──────────────────────────────────────────────── */}
            <div className={['flex flex-col gap-2.5 py-3', APP_BG.canvas].join(' ')}>
                {Array.from({ length: rows }).map((_, ri) => {
                    const widths = ri % 2 === 0 ? WIDE_WIDTHS : NARROW_WIDTHS;
                    const d0 = jitter(ri, 0, ri * 0.07);
                    // Every 3rd card: last body field spans full width (like noTruncate)
                    const lastSpans = bodyCols >= 3 && ri % 3 === 2;
                    // Cards fade out slightly towards the bottom (suggests more data below)
                    const cardOpacity = Math.max(1 - ri * 0.045, 0.5);

                    return (
                        <div
                            key={ri}
                            style={{ opacity: cardOpacity }}
                            className={[
                                'rounded-2xl border overflow-hidden',
                                'border-gray-200/90 dark:border-white/[0.07]',
                                'shadow-[0_1px_4px_0_rgba(0,0,0,0.05)] dark:shadow-[0_1px_8px_0_rgba(0,0,0,0.22)]',
                                APP_BG.base,
                            ].join(' ')}
                        >
                            {/* ── Header ────────────────────────────────────── */}
                            <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
                                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                                    {/* column label */}
                                    <div
                                        className="sk-bar h-2"
                                        style={{ maxWidth: '38%', '--sk-delay': `${d0.toFixed(3)}s` } as React.CSSProperties}
                                    />
                                    {/* primary value — taller */}
                                    <div
                                        className="sk-bar h-[14px]"
                                        style={{ maxWidth: `${widths[0]}%`, '--sk-delay': `${d0.toFixed(3)}s` } as React.CSSProperties}
                                    />
                                    {/* subtitle line — every other card */}
                                    {ri % 2 === 0 && (
                                        <div
                                            className="sk-bar h-[10px] opacity-60"
                                            style={{
                                                maxWidth: `${Math.round(widths[0] * 0.62)}%`,
                                                '--sk-delay': `${jitter(ri, 1, d0 + 0.02).toFixed(3)}s`,
                                            } as React.CSSProperties}
                                        />
                                    )}
                                </div>
                                {/* action button — rounded square (matches real kebab/action button) */}
                                <div
                                    className="sk-rect h-7 w-7 shrink-0"
                                    style={{ '--sk-delay': `${(d0 + 0.03).toFixed(3)}s` } as React.CSSProperties}
                                />
                            </div>

                            {/* ── Hairline divider ──────────────────────────── */}
                            {bodyCols > 0 && (
                                <div className="mx-4 h-px bg-gray-100 dark:bg-white/[0.05]" />
                            )}

                            {/* ── Body 2-col grid ───────────────────────────── */}
                            {bodyCols > 0 && (
                                <dl className="grid grid-cols-2 gap-x-5 gap-y-4 px-4 pt-3 pb-4">
                                    {Array.from({ length: bodyCols }).map((_, ci) => {
                                        const delay = `${jitter(ri, ci + 1, d0 + (ci + 1) * 0.04).toFixed(3)}s`;
                                        const vw = (ci + 1) < widths.length ? widths[ci + 1] : 55;
                                        const spanFull = lastSpans && ci === bodyCols - 1;

                                        return (
                                            <div
                                                key={ci}
                                                className={['min-w-0 flex flex-col gap-1.5', spanFull ? 'col-span-2' : ''].join(' ')}
                                            >
                                                <div
                                                    className="sk-bar h-2"
                                                    style={{ maxWidth: '52%', '--sk-delay': delay } as React.CSSProperties}
                                                />
                                                <div
                                                    className="sk-bar h-3"
                                                    style={{ maxWidth: `${vw}%`, '--sk-delay': delay } as React.CSSProperties}
                                                />
                                            </div>
                                        );
                                    })}
                                </dl>
                            )}

                            {/* ── Footer (expandable only) ───────────────────── */}
                            {hasExpand && (
                                <>
                                    <div className="h-px bg-gray-100 dark:bg-white/[0.05]" />
                                    <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/60 dark:bg-white/[0.015]">
                                        {/* #N counter */}
                                        <div
                                            className="sk-bar h-2.5"
                                            style={{ width: '20px', '--sk-delay': `${d0.toFixed(3)}s` } as React.CSSProperties}
                                        />
                                        {/* "Details" pill button */}
                                        <div
                                            className="sk-bar h-7 rounded-full"
                                            style={{ width: '88px', '--sk-delay': `${jitter(ri, 5, d0 + 0.05).toFixed(3)}s` } as React.CSSProperties}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Bottom spacer — matches MobileCardView real view */}
            <div className="h-14" aria-hidden="true" />
        </>
    );
};

export default SkeletonCards;

